import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";
import { withAssetUrls } from "../../services/focusApi";

export const SLIDE_W = 1280;
export const SLIDE_H = 720;

/**
 * Une diapo Focus tech : document HTML complet (backend focus_tech.slide_document) affiché dans une iframe isolée
 * (`sandbox="allow-scripts"`, origine opaque : ni session, ni stockage, ni accès à Deepflow), à 1280 × 720 réduits
 * à la place disponible. Le pont injecté dans la diapo dialogue par postMessage :
 *  - role : « presenter » relève les gestes (onEvents) et l'état déclaré (onState) ; « viewer » rejoue ce qu'on lui
 *    envoie (replay / applyState) ; « free » : chacun pour soi ;
 *  - touches de navigation remontées (onKey) ; « ready » quand la page a démarré (onReady).
 * `locked` pose un voile qui empêche le participant qui suit le présentateur de toucher la diapo.
 */
const SlideFrame = forwardRef(function SlideFrame(
  { document, role = "free", locked = false, onEvents, onState, onKey, onReady, fit = "width", title, className = "" },
  ref
) {
  const boxRef = useRef(null);
  const frameRef = useRef(null);
  const readyRef = useRef(false);
  const roleRef = useRef(role);
  const handlers = useRef({});
  handlers.current = { onEvents, onState, onKey, onReady };
  const [scale, setScale] = useState(0);

  const post = (message) => {
    const target = frameRef.current?.contentWindow;
    if (target) target.postMessage(message, "*");
  };

  useImperativeHandle(ref, () => ({
    replay: (events) => {
      if (readyRef.current && events?.length) post({ deepflowFocus: "replay", events });
    },
    applyState: (state) => {
      if (readyRef.current && state != null) post({ deepflowFocus: "state", state });
    },
    // Rattrapage (arrivée en cours, retour au direct, rechargement du présentateur) : la diapo rejoue d'abord en
    // spectatrice, puis reprend son rôle.
    catchUp: (events, state) => {
      if (!readyRef.current) return;
      post({ deepflowFocus: "role", role: "viewer" });
      if (events?.length) post({ deepflowFocus: "replay", events });
      if (state != null) post({ deepflowFocus: "state", state });
      post({ deepflowFocus: "role", role: roleRef.current });
    },
    isReady: () => readyRef.current,
  }));

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return undefined;
    const measure = () => {
      const width = box.clientWidth;
      const height = box.clientHeight;
      const next = fit === "contain" && height ? Math.min(width / SLIDE_W, height / SLIDE_H) : width / SLIDE_W;
      setScale(next > 0 ? next : 0);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, [fit]);

  useEffect(() => {
    roleRef.current = role;
    if (readyRef.current) post({ deepflowFocus: "role", role });
  }, [role]);

  useEffect(() => {
    readyRef.current = false;
    const onMessage = (event) => {
      if (!frameRef.current || event.source !== frameRef.current.contentWindow) return;
      const data = event.data || {};
      const { onEvents: events, onState: state, onKey: key, onReady: ready } = handlers.current;
      if (data.deepflowFocus === "ready") {
        readyRef.current = true;
        post({ deepflowFocus: "role", role: roleRef.current });
        if (ready) ready();
      } else if (data.deepflowFocus === "events" && Array.isArray(data.events)) {
        if (events) events(data.events);
      } else if (data.deepflowFocus === "state") {
        if (state) state(data.state);
      } else if (data.deepflowFocus === "key" && typeof data.key === "string") {
        if (key) key(data.key);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [document]);

  const width = SLIDE_W * scale;
  const height = SLIDE_H * scale;
  return (
    <div
      ref={boxRef}
      className={`relative w-full overflow-hidden ${fit === "contain" ? "h-full" : ""} ${className}`}
      style={fit === "contain" ? undefined : { aspectRatio: "16 / 9" }}
    >
      <div
        className="absolute bg-white shadow-sm"
        style={{ width, height, left: `calc(50% - ${width / 2}px)`, top: fit === "contain" ? `calc(50% - ${height / 2}px)` : 0 }}
      >
        {document ? (
          <iframe
            ref={frameRef}
            key={document.length + ":" + document.slice(0, 64)}
            title={title || "Diapo Focus tech"}
            sandbox="allow-scripts"
            srcDoc={withAssetUrls(document)}
            style={{ width: SLIDE_W, height: SLIDE_H, border: 0, transform: `scale(${scale})`, transformOrigin: "0 0" }}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-slate-400">Diapo vide</div>
        )}
        {locked && <div className="absolute inset-0 cursor-default" aria-hidden="true" />}
      </div>
    </div>
  );
});

export default SlideFrame;
